import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import { BsBriefcase, BsCashCoin, BsFileEarmarkText, BsHeartPulse, BsPersonVcard } from "react-icons/bs";
import Handler from "../../Helpers/Handler";
import { textCapitalize } from "../../Helpers/Utils";
import { Skeleton } from "../../Components/Needed";
import {
    emptyStaffForm, emptyAddress, GENERAL_FIELDS, FINANCIAL_FIELDS,
    toDateInputValue, isAddressFilled, isAddressComplete,
} from "./staffFormHelpers";

const ChipListEditor = ({ label, values, onChange, placeholder }) => {
    const [draft, setDraft] = useState("");
    const add = () => {
        const value = draft.trim();
        if (value && !values.includes(value)) onChange([...values, value]);
        setDraft("");
    };
    const remove = (value) => onChange(values.filter((v) => v !== value));
    const onKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            add();
        }
    };
    return (
        <div className="col-12 col-md-6">
            <label className="form-label">{label}</label>
            <div className="d-flex flex-wrap gap-2 mb-2">
                {values.map((v) => (
                    <span key={v} className="badge rounded-pill text-bg-light border d-inline-flex align-items-center gap-1">
                        {v}
                        <button type="button" className="btn-close" style={{ fontSize: "0.55rem" }} aria-label={`Remove ${v}`} onClick={() => remove(v)}></button>
                    </span>
                ))}
            </div>
            <div className="input-group">
                <input type="text" className="form-control" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKeyDown} placeholder={placeholder} />
                <button type="button" className="btn btn-outline-secondary" onClick={add}>Add</button>
            </div>
        </div>
    );
};

const TABS = [
    { key: "personal", name: "Personal", icon: <BsPersonVcard /> },
    { key: "employment", name: "Employment", icon: <BsBriefcase /> },
    { key: "financial", name: "Financial", icon: <BsCashCoin /> },
    { key: "documents", name: "Documents & Education", icon: <BsFileEarmarkText /> },
    { key: "emergency", name: "Emergency Contact", icon: <BsHeartPulse /> },
];

const StaffFormPage = () => {
    const { staffId } = useParams();
    const navigate = useNavigate();
    const mode = staffId ? "edit" : "create";

    const [activeTab, setActiveTab] = useState("personal");
    const [form, setForm] = useState(emptyStaffForm);
    const [original, setOriginal] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [hubs, setHubs] = useState([]);
    const [roleOptions, setRoleOptions] = useState([]);
    const [loading, setLoading] = useState(mode === "edit");
    const [saving, setSaving] = useState(false);

    const setTop = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
    const setNested = (group, key, value) => setForm((prev) => ({ ...prev, [group]: { ...prev[group], [key]: value } }));

    useEffect(() => {
        (async () => {
            const [departmentsResponse, hubsResponse] = await Promise.all([
                Handler({ method: "GET", url: "/admin/staff/department/active" }),
                Handler({ method: "GET", url: "/admin/hub/active" }),
            ]);
            setDepartments(departmentsResponse?.success ? departmentsResponse?.data?.data || [] : []);
            setHubs(hubsResponse?.success ? hubsResponse?.data?.data || [] : []);
        })();
    }, []);

    // Edit mode: staffDetail doesn't carry departmentId directly (only the
    // joined departmentName) - a role belongs to exactly one department, so
    // the role's own detail record is the one place to recover it and
    // pre-select the department dropdown correctly.
    useEffect(() => {
        if (mode !== "edit") return;
        (async () => {
            setLoading(true);
            const staffResponse = await Handler({ method: "GET", url: `/admin/staff/${staffId}` });
            if (!staffResponse?.success) {
                toast.error(textCapitalize(staffResponse?.data?.message || "Staff not found"));
                setLoading(false);
                return;
            }
            const staff = staffResponse.data.data;
            let departmentId = "";
            if (staff.roleId) {
                const roleResponse = await Handler({ method: "GET", url: `/admin/staff/role/${staff.roleId}` });
                departmentId = roleResponse?.success ? roleResponse?.data?.data?.departmentId || "" : "";
            }
            setOriginal(staff);
            setForm({
                employeeId: staff.employeeId || "",
                name: staff.name || "",
                dateOfBirth: toDateInputValue(staff.dateOfBirth),
                gender: staff.gender || "",
                bloodGroup: staff.bloodGroup || "",
                maritalStatus: staff.maritalStatus || "",
                profilePhoto: staff.profilePhoto || "",
                phone: staff.phone || "",
                alternatePhone: staff.alternatePhone || "",
                email: staff.email || "",
                currentAddress: { ...emptyAddress, ...(staff.currentAddress || {}) },
                permanentAddress: { ...emptyAddress, ...(staff.permanentAddress || {}) },
                sameAsCurrent: !!staff.sameAsCurrent,
                designation: staff.designation || "",
                departmentId,
                roleId: staff.roleId || "",
                hubId: staff.hubId || "",
                employmentType: staff.employmentType || "",
                dateOfJoining: toDateInputValue(staff.dateOfJoining),
                probationEndDate: toDateInputValue(staff.probationEndDate),
                reportingTo: staff.reportingTo || "",
                aadhaarNumber: staff.aadhaarNumber || "",
                panNumber: staff.panNumber || "",
                drivingLicenseNumber: staff.drivingLicenseNumber || "",
                drivingLicenseExpiry: toDateInputValue(staff.drivingLicenseExpiry),
                baseSalary: staff.baseSalary ?? "",
                allowances: staff.allowances ?? "",
                bankDetails: { accountNumber: "", bankName: "", bankHolder: "", ifsc: "", ...(staff.bankDetails || {}) },
                uanNumber: staff.uanNumber || "",
                pfNumber: staff.pfNumber || "",
                esiNumber: staff.esiNumber || "",
                emergencyContact: { name: "", relationship: "", phone: "", email: "", ...(staff.emergencyContact || {}) },
                highestEducation: staff.highestEducation || "",
                certifications: staff.certifications || [],
                languagesKnown: staff.languagesKnown || [],
                password: "",
            });
            setLoading(false);
        })();
    }, [mode, staffId]);

    // Whenever the chosen department changes, refresh the role dropdown to
    // that department's active roles, and drop a previously-picked role that
    // no longer belongs to it (mirrors the same guard used on Role.tasks).
    const loadRolesForDepartment = useCallback(async (departmentId, keepRoleId) => {
        if (!departmentId) {
            setRoleOptions([]);
            return;
        }
        const response = await Handler({ method: "GET", url: `/admin/staff/department/${departmentId}/roles` });
        const list = response?.success ? response?.data?.data || [] : [];
        setRoleOptions(list);
        if (keepRoleId && !list.some((r) => r.roleId === keepRoleId)) {
            setTop("roleId", "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (form.departmentId) loadRolesForDepartment(form.departmentId, form.roleId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.departmentId]);

    const onDepartmentChange = (departmentId) => {
        setTop("departmentId", departmentId);
        setTop("roleId", "");
    };

    const validate = () => {
        if (mode === "create") {
            // 2026-09-10 (second correction, same day): department + role are
            // back in registration and mandatory. Everything else (email,
            // designation, hubId, employmentType, password, and all the
            // optional extras) is still filled in later via Edit / role
            // reassignment / password reset. See staffRegister on the backend.
            const required = { employeeId: form.employeeId, name: form.name, gender: form.gender, dateOfBirth: form.dateOfBirth, bloodGroup: form.bloodGroup, phone: form.phone, dateOfJoining: form.dateOfJoining, departmentId: form.departmentId, roleId: form.roleId, hubId: form.hubId };
            for (const [field, value] of Object.entries(required)) {
                if (!String(value || "").trim()) return `${field} is required`;
            }
        } else {
            if (!form.name.trim()) return "Name is required";
            if (!form.designation.trim()) return "Designation is required";
        }
        if (isAddressFilled(form.currentAddress) && !isAddressComplete(form.currentAddress)) {
            return "Fill in all current address fields, or leave all of them blank";
        }
        if (!form.sameAsCurrent && isAddressFilled(form.permanentAddress) && !isAddressComplete(form.permanentAddress)) {
            return "Fill in all permanent address fields, or leave all of them blank";
        }
        return "";
    };

    const buildAddressPayload = (address) => (isAddressComplete(address) ? address : undefined);

    const onSubmit = async () => {
        const error = validate();
        if (error) {
            toast.error(error);
            return;
        }

        setSaving(true);
        if (mode === "create") {
            // 2026-09-10 (second correction, same day): roleId goes back in the
            // registration payload (departmentId is frontend-only, used just to
            // filter the role dropdown - the backend only stores roleId).
            // Everything else is still added later through Edit / role
            // reassignment / password reset, once the record exists.
            const payload = {
                employeeId: form.employeeId.trim(),
                name: form.name.trim(),
                dateOfBirth: form.dateOfBirth,
                gender: form.gender,
                bloodGroup: form.bloodGroup.trim(),
                phone: form.phone.trim(),
                dateOfJoining: form.dateOfJoining,
                roleId: form.roleId,
                hubId: form.hubId,
            };
            const response = await Handler({ method: "post", url: "/admin/staff/register", data: payload });
            if (response?.success) {
                toast.success(response?.data?.message || "Staff added successfully");
                navigate(`/staff/${response?.data?.data?.staffId}`);
            } else {
                toast.error(textCapitalize(response?.data?.message || "Unable to add staff"));
            }
        } else {
            const currentAddress = buildAddressPayload(form.currentAddress);
            const permanentAddress = form.sameAsCurrent ? currentAddress : buildAddressPayload(form.permanentAddress);
            const candidate = {
                name: form.name.trim(),
                dateOfBirth: form.dateOfBirth || undefined,
                gender: form.gender || undefined,
                bloodGroup: form.bloodGroup || undefined,
                maritalStatus: form.maritalStatus || undefined,
                profilePhoto: form.profilePhoto.trim() || undefined,
                phone: form.phone.trim(),
                alternatePhone: form.alternatePhone.trim() || undefined,
                email: form.email.trim(),
                currentAddress,
                permanentAddress,
                designation: form.designation.trim(),
                // hubId/roleId added 2026-09-10 (third pass) - both dropdowns
                // already existed in the Employment tab below but were never
                // actually part of the save payload until now.
                hubId: form.hubId || undefined,
                roleId: form.roleId || undefined,
                employmentType: form.employmentType || undefined,
                probationEndDate: form.probationEndDate || undefined,
                reportingTo: form.reportingTo.trim() || undefined,
                emergencyContact: Object.values(form.emergencyContact).some((v) => v.trim()) ? form.emergencyContact : undefined,
                highestEducation: form.highestEducation.trim() || undefined,
                certifications: form.certifications,
                languagesKnown: form.languagesKnown,
                aadhaarNumber: form.aadhaarNumber.trim() || undefined,
                panNumber: form.panNumber.trim() || undefined,
                drivingLicenseNumber: form.drivingLicenseNumber.trim() || undefined,
                drivingLicenseExpiry: form.drivingLicenseExpiry || undefined,
            };
            const generalPayload = {};
            GENERAL_FIELDS.forEach((field) => {
                const value = candidate[field];
                const same = JSON.stringify(value ?? null) === JSON.stringify(original?.[field] ?? null);
                if (!same && value !== undefined) generalPayload[field] = value;
            });

            const financialCandidate = {
                baseSalary: form.baseSalary !== "" ? Number(form.baseSalary) : undefined,
                allowances: form.allowances !== "" ? Number(form.allowances) : undefined,
                bankDetails: Object.values(form.bankDetails).some((v) => v.trim()) ? form.bankDetails : undefined,
                uanNumber: form.uanNumber.trim() || undefined,
                pfNumber: form.pfNumber.trim() || undefined,
                esiNumber: form.esiNumber.trim() || undefined,
            };
            const financialPayload = {};
            FINANCIAL_FIELDS.forEach((field) => {
                const value = financialCandidate[field];
                const same = JSON.stringify(value ?? null) === JSON.stringify(original?.[field] ?? null);
                if (!same && value !== undefined) financialPayload[field] = value;
            });

            if (!Object.keys(generalPayload).length && !Object.keys(financialPayload).length) {
                toast.info("No changes to save");
                setSaving(false);
                return;
            }

            const results = [];
            if (Object.keys(generalPayload).length) {
                results.push(await Handler({ method: "patch", url: `/admin/staff/${staffId}`, data: generalPayload }));
            }
            if (Object.keys(financialPayload).length) {
                results.push(await Handler({ method: "patch", url: `/admin/staff/${staffId}/financial`, data: financialPayload }));
            }
            const allOk = results.every((r) => r?.success);
            if (allOk) {
                toast.success("Staff details updated");
                navigate(`/staff/${staffId}`);
            } else {
                const failed = results.find((r) => !r?.success);
                toast.error(textCapitalize(failed?.data?.message || "Unable to save some changes"));
            }
        }
        setSaving(false);
    };

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="30%" className="mb-3" />
                <Skeleton height={220} />
            </div>
        );
    }

    return (
        <>
            <button
                type="button"
                className="btn btn-link text-decoration-none px-3 mb-3 d-inline-flex align-items-center gap-2"
                onClick={() => navigate(mode === "edit" ? `/staff/${staffId}` : "/staff")}
            >
                <FaArrowLeft /> {mode === "edit" ? "Back to profile" : "Back to staff"}
            </button>

            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                <div>
                    <h4 className="fw-semibold mb-1">{mode === "edit" ? `Edit ${original?.name || "Staff"}` : "Add Staff"}</h4>
                    <p className="text-muted mb-0">{mode === "edit" ? "Update this staff member's details." : "Register a new staff member."}</p>
                </div>
                <div className="d-flex gap-2">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(mode === "edit" ? `/staff/${staffId}` : "/staff")}>Cancel</button>
                    <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving}>
                        {saving ? "Saving..." : mode === "edit" ? "Save changes" : "Create staff"}
                    </button>
                </div>
            </div>

            {mode === "create" ? (
                // 2026-09-10: registration collects exactly these 7 fields - no
                // tabs needed, everything else is added later from the profile
                // page once the record exists (Edit / role assignment / password
                // reset).
                <div className="settings-card">
                    <div className="row g-3">
                        <div className="col-12 col-md-4">
                            <label className="form-label">Employee ID</label>
                            <input type="text" className="form-control" value={form.employeeId} onChange={(e) => setTop("employeeId", e.target.value)} placeholder="e.g. EMP-1042" />
                        </div>
                        <div className="col-12 col-md-4">
                            <label className="form-label">Full name</label>
                            <input type="text" className="form-control" value={form.name} onChange={(e) => setTop("name", e.target.value)} />
                        </div>
                        <div className="col-12 col-md-4">
                            <label className="form-label">Phone</label>
                            <input type="text" className="form-control" value={form.phone} onChange={(e) => setTop("phone", e.target.value)} />
                        </div>
                        <div className="col-12 col-md-4">
                            <label className="form-label">Gender</label>
                            <select className="form-select" value={form.gender} onChange={(e) => setTop("gender", e.target.value)}>
                                <option value="" disabled>Select</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div className="col-12 col-md-4">
                            <label className="form-label">Date of birth</label>
                            <input type="date" className="form-control" value={form.dateOfBirth} onChange={(e) => setTop("dateOfBirth", e.target.value)} />
                        </div>
                        <div className="col-12 col-md-4">
                            <label className="form-label">Blood group</label>
                            <input type="text" className="form-control" value={form.bloodGroup} onChange={(e) => setTop("bloodGroup", e.target.value)} placeholder="e.g. O+" />
                        </div>
                        <div className="col-12 col-md-4">
                            <label className="form-label">Date of joining</label>
                            <input type="date" className="form-control" value={form.dateOfJoining} onChange={(e) => setTop("dateOfJoining", e.target.value)} />
                        </div>
                        <div className="col-12 col-md-4">
                            <label className="form-label">Department</label>
                            <select className="form-select" value={form.departmentId} onChange={(e) => onDepartmentChange(e.target.value)}>
                                <option value="" disabled>Select department</option>
                                {departments.map((d) => (<option key={d.departmentId} value={d.departmentId}>{d.departmentName}</option>))}
                            </select>
                        </div>
                        <div className="col-12 col-md-4">
                            <label className="form-label">Role</label>
                            <select className="form-select" value={form.roleId} onChange={(e) => setTop("roleId", e.target.value)} disabled={!form.departmentId}>
                                <option value="" disabled>{form.departmentId ? "Select role" : "Select a department first"}</option>
                                {roleOptions.map((r) => (<option key={r.roleId} value={r.roleId}>{r.roleName}</option>))}
                            </select>
                        </div>
                        <div className="col-12 col-md-4">
                            <label className="form-label">Hub</label>
                            <select className="form-select" value={form.hubId} onChange={(e) => setTop("hubId", e.target.value)}>
                                <option value="" disabled>Select hub</option>
                                {hubs.map((h) => (<option key={h.hubId} value={h.hubId}>{[h.buildingNo, h.area, h.city].filter(Boolean).join(", ") || h.hubId}</option>))}
                            </select>
                        </div>
                        <div className="col-12">
                            <div className="alert alert-secondary mb-0 small">
                                Email, designation, employment type, and password are set afterwards from this staff member's profile page.
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
            <div className="settings-layout">
                <div className="settings-tabs" role="tablist" aria-orientation="vertical">
                    {TABS.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === tab.key}
                            className={`settings-tab-btn ${activeTab === tab.key ? "active" : ""}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            <span className="settings-tab-icon">{tab.icon}</span>
                            {tab.name}
                        </button>
                    ))}
                </div>

                <div className="settings-tab-panel">
                    {activeTab === "personal" ? (
                        <div className="row g-3">
                            <div className="col-12 col-md-4">
                                <label className="form-label">Full name</label>
                                <input type="text" className="form-control" value={form.name} onChange={(e) => setTop("name", e.target.value)} />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Gender</label>
                                <select className="form-select" value={form.gender} onChange={(e) => setTop("gender", e.target.value)} disabled={mode === "edit"}>
                                    <option value="" disabled>Select</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Date of birth</label>
                                <input type="date" className="form-control" value={form.dateOfBirth} onChange={(e) => setTop("dateOfBirth", e.target.value)} />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Blood group</label>
                                <input type="text" className="form-control" value={form.bloodGroup} onChange={(e) => setTop("bloodGroup", e.target.value)} placeholder="e.g. O+" />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Marital status</label>
                                <select className="form-select" value={form.maritalStatus} onChange={(e) => setTop("maritalStatus", e.target.value)}>
                                    <option value="">Not specified</option>
                                    <option value="single">Single</option>
                                    <option value="married">Married</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Phone</label>
                                <input type="text" className="form-control" value={form.phone} onChange={(e) => setTop("phone", e.target.value)} />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Alternate phone</label>
                                <input type="text" className="form-control" value={form.alternatePhone} onChange={(e) => setTop("alternatePhone", e.target.value)} />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Email</label>
                                <input type="email" className="form-control" value={form.email} onChange={(e) => setTop("email", e.target.value)} />
                            </div>

                            <div className="col-12"><hr /></div>

                            <div className="col-12 col-md-6">
                                <label className="form-label">Current address</label>
                                <div className="row g-2">
                                    <div className="col-12"><input type="text" className="form-control" placeholder="Street" value={form.currentAddress.street} onChange={(e) => setNested("currentAddress", "street", e.target.value)} /></div>
                                    <div className="col-6"><input type="text" className="form-control" placeholder="City" value={form.currentAddress.city} onChange={(e) => setNested("currentAddress", "city", e.target.value)} /></div>
                                    <div className="col-6"><input type="text" className="form-control" placeholder="State" value={form.currentAddress.state} onChange={(e) => setNested("currentAddress", "state", e.target.value)} /></div>
                                    <div className="col-6"><input type="text" className="form-control" placeholder="Postal code" value={form.currentAddress.postalCode} onChange={(e) => setNested("currentAddress", "postalCode", e.target.value)} /></div>
                                    <div className="col-6"><input type="text" className="form-control" placeholder="Country" value={form.currentAddress.country} onChange={(e) => setNested("currentAddress", "country", e.target.value)} /></div>
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="d-flex align-items-center justify-content-between">
                                    <label className="form-label mb-0">Permanent address</label>
                                    <div className="form-check">
                                        <input type="checkbox" className="form-check-input" id="sameAsCurrent" checked={form.sameAsCurrent} onChange={(e) => setTop("sameAsCurrent", e.target.checked)} />
                                        <label className="form-check-label small" htmlFor="sameAsCurrent">Same as current</label>
                                    </div>
                                </div>
                                {form.sameAsCurrent ? (
                                    <div className="text-muted small mt-4">Will be saved same as the current address above.</div>
                                ) : (
                                    <div className="row g-2 mt-0">
                                        <div className="col-12"><input type="text" className="form-control" placeholder="Street" value={form.permanentAddress.street} onChange={(e) => setNested("permanentAddress", "street", e.target.value)} /></div>
                                        <div className="col-6"><input type="text" className="form-control" placeholder="City" value={form.permanentAddress.city} onChange={(e) => setNested("permanentAddress", "city", e.target.value)} /></div>
                                        <div className="col-6"><input type="text" className="form-control" placeholder="State" value={form.permanentAddress.state} onChange={(e) => setNested("permanentAddress", "state", e.target.value)} /></div>
                                        <div className="col-6"><input type="text" className="form-control" placeholder="Postal code" value={form.permanentAddress.postalCode} onChange={(e) => setNested("permanentAddress", "postalCode", e.target.value)} /></div>
                                        <div className="col-6"><input type="text" className="form-control" placeholder="Country" value={form.permanentAddress.country} onChange={(e) => setNested("permanentAddress", "country", e.target.value)} /></div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : null}

                    {activeTab === "employment" ? (
                        <div className="row g-3">
                            <div className="col-12 col-md-4">
                                <label className="form-label">Designation</label>
                                <input type="text" className="form-control" value={form.designation} onChange={(e) => setTop("designation", e.target.value)} placeholder="e.g. Hub Supervisor" />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Department</label>
                                <select className="form-select" value={form.departmentId} onChange={(e) => onDepartmentChange(e.target.value)}>
                                    <option value="" disabled>Select department</option>
                                    {departments.map((d) => (<option key={d.departmentId} value={d.departmentId}>{d.departmentName}</option>))}
                                </select>
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Role</label>
                                <select className="form-select" value={form.roleId} onChange={(e) => setTop("roleId", e.target.value)} disabled={!form.departmentId}>
                                    <option value="" disabled>{form.departmentId ? "Select role" : "Select a department first"}</option>
                                    {roleOptions.map((r) => (<option key={r.roleId} value={r.roleId}>{r.roleName}</option>))}
                                </select>
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Hub</label>
                                <select className="form-select" value={form.hubId} onChange={(e) => setTop("hubId", e.target.value)}>
                                    <option value="" disabled>Select hub</option>
                                    {hubs.map((h) => (<option key={h.hubId} value={h.hubId}>{[h.buildingNo, h.area, h.city].filter(Boolean).join(", ") || h.hubId}</option>))}
                                </select>
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Employment type</label>
                                <select className="form-select" value={form.employmentType} onChange={(e) => setTop("employmentType", e.target.value)}>
                                    <option value="" disabled>Select type</option>
                                    <option value="full-time">Full-time</option>
                                    <option value="part-time">Part-time</option>
                                    <option value="contract">Contract</option>
                                    <option value="intern">Intern</option>
                                </select>
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Date of joining</label>
                                <input type="date" className="form-control" value={form.dateOfJoining} onChange={(e) => setTop("dateOfJoining", e.target.value)} disabled={mode === "edit"} />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Probation end date</label>
                                <input type="date" className="form-control" value={form.probationEndDate} onChange={(e) => setTop("probationEndDate", e.target.value)} />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Reports to (staff ID)</label>
                                <input type="text" className="form-control" value={form.reportingTo} onChange={(e) => setTop("reportingTo", e.target.value)} placeholder="e.g. SP0004" />
                            </div>
                            {mode === "edit" ? (
                                <div className="col-12">
                                    <div className="alert alert-secondary mb-0 small">
                                        Employment status and password reset are managed from this staff member's profile page, not here. Department, role, and hub can now be changed right here too.
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {activeTab === "financial" ? (
                        <div className="row g-3">
                            <div className="col-12 col-md-4">
                                <label className="form-label">Base salary</label>
                                <input type="number" min="0" className="form-control" value={form.baseSalary} onChange={(e) => setTop("baseSalary", e.target.value)} />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Allowances</label>
                                <input type="number" min="0" className="form-control" value={form.allowances} onChange={(e) => setTop("allowances", e.target.value)} />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">UAN number</label>
                                <input type="text" className="form-control" value={form.uanNumber} onChange={(e) => setTop("uanNumber", e.target.value)} />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">PF number</label>
                                <input type="text" className="form-control" value={form.pfNumber} onChange={(e) => setTop("pfNumber", e.target.value)} />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">ESI number</label>
                                <input type="text" className="form-control" value={form.esiNumber} onChange={(e) => setTop("esiNumber", e.target.value)} />
                            </div>

                            <div className="col-12"><hr /><label className="form-label">Bank details</label></div>
                            <div className="col-12 col-md-6">
                                <input type="text" className="form-control mb-2" placeholder="Account number" value={form.bankDetails.accountNumber} onChange={(e) => setNested("bankDetails", "accountNumber", e.target.value)} />
                                <input type="text" className="form-control" placeholder="IFSC" value={form.bankDetails.ifsc} onChange={(e) => setNested("bankDetails", "ifsc", e.target.value)} />
                            </div>
                            <div className="col-12 col-md-6">
                                <input type="text" className="form-control mb-2" placeholder="Bank name" value={form.bankDetails.bankName} onChange={(e) => setNested("bankDetails", "bankName", e.target.value)} />
                                <input type="text" className="form-control" placeholder="Account holder name" value={form.bankDetails.bankHolder} onChange={(e) => setNested("bankDetails", "bankHolder", e.target.value)} />
                            </div>
                            {mode === "edit" ? <div className="col-12 text-muted small">Salary and bank fields save separately from the rest of this form.</div> : null}
                        </div>
                    ) : null}

                    {activeTab === "documents" ? (
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <label className="form-label">Aadhaar number</label>
                                <input type="text" className="form-control" value={form.aadhaarNumber} onChange={(e) => setTop("aadhaarNumber", e.target.value)} />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">PAN number</label>
                                <input type="text" className="form-control" value={form.panNumber} onChange={(e) => setTop("panNumber", e.target.value)} />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Driving licence number</label>
                                <input type="text" className="form-control" value={form.drivingLicenseNumber} onChange={(e) => setTop("drivingLicenseNumber", e.target.value)} />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Driving licence expiry</label>
                                <input type="date" className="form-control" value={form.drivingLicenseExpiry} onChange={(e) => setTop("drivingLicenseExpiry", e.target.value)} />
                            </div>
                            <div className="col-12">
                                <label className="form-label">Highest education</label>
                                <input type="text" className="form-control" value={form.highestEducation} onChange={(e) => setTop("highestEducation", e.target.value)} placeholder="e.g. B.Com, Loyola College" />
                            </div>
                            <ChipListEditor label="Certifications" values={form.certifications} onChange={(v) => setTop("certifications", v)} placeholder="e.g. Defensive driving - press Enter to add" />
                            <ChipListEditor label="Languages known" values={form.languagesKnown} onChange={(v) => setTop("languagesKnown", v)} placeholder="e.g. Tamil - press Enter to add" />
                        </div>
                    ) : null}

                    {activeTab === "emergency" ? (
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <label className="form-label">Contact name</label>
                                <input type="text" className="form-control" value={form.emergencyContact.name} onChange={(e) => setNested("emergencyContact", "name", e.target.value)} />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Relationship</label>
                                <input type="text" className="form-control" value={form.emergencyContact.relationship} onChange={(e) => setNested("emergencyContact", "relationship", e.target.value)} placeholder="e.g. Spouse, Parent" />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Phone</label>
                                <input type="text" className="form-control" value={form.emergencyContact.phone} onChange={(e) => setNested("emergencyContact", "phone", e.target.value)} />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Email</label>
                                <input type="email" className="form-control" value={form.emergencyContact.email} onChange={(e) => setNested("emergencyContact", "email", e.target.value)} />
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
            )}
        </>
    );
};

export default StaffFormPage;
